import { useRecoilState, useRecoilValue } from 'recoil';
import { useAnalytics } from './useAnalytics';
import currency from 'currency.js';
import { useState } from 'react';
import { persistedFlowState } from './persistedFlowState';
import { useRouting } from './useRouting';
import { uuid } from '../utils/uuid';
import { BenefitCode, PricingTier, ServiceOption, Clinic } from '../../__generated__/graphql';
import { PersistedFlow } from './persistedFlowType';
import { authenticationStateAtom } from '../state/authenticationState';

export const usePersistantFlow = () => {
  const { navigate } = useRouting();

  const [promoCode, setPromoCode] = useState<BenefitCode | null>(null);

  const { isAuthenticated, user } = useRecoilValue(authenticationStateAtom);

  const [state, setState] = useRecoilState(persistedFlowState);

  const clearFlow = () => setState(null);

  const { trackEvent } = useAnalytics();

  const startFlow = (
    pricing: PricingTier,
    service: ServiceOption,
    clinic: Clinic,
    region: string | null | undefined,
    formID?: string,
    formValues?: Record<string, string>,
  ) => {
    let event: PersistedFlow = {
      pricing,
      service,
      clinic,
      region,
      selectedOption: null,
      id: uuid(),
      forms: formID && formValues ? { [formID]: formValues } : {},
      subscribe: false,
      startedAt: new Date(),
      startedAuthenticated: isAuthenticated,
      step: formID && formValues ? '/start/info' : '/start',
    };

    if (isAuthenticated) {
      event.region = user?.state;
    }

    setState(event);

    trackEvent('started_request', event);

    if (formID && formValues) {
      navigate(`/start/info`);
    } else {
      navigate(`/start`);
    }
  };

  const setClinic = (clinic: Clinic) => {
    setState((prev) => {
      return { ...prev, clinic };
    });
  };

  const setPricing = (pricing: PricingTier) => {
    setState((prev) => {
      return { ...prev, pricing };
    });
  };

  const setForm = (formID: string, formValues) => {
    setState((prev) => {
      return { ...prev, region: user?.state, forms: { ...prev.forms, [formID]: formValues } };
    });
  };

  const setStep = (step: string) => {
    setState((prev) => {
      return { ...prev, region: user?.state, step };
    });
  };

  const setAppointment = (appointment) => {
    setState((prev) => {
      return { ...prev, appointment };
    });
  };

  const setSubscribe = (subscribe) => {
    setState((prev) => {
      return { ...prev, subscribe };
    });
  };

  const totalAmount = () => {
    const additions =
      state?.service?.options?.find((b) => b.id === state.selectedOption)?.price ?? 0;

    let total = currency(state.pricing.amount).add(additions);

    if (promoCode && promoCode?.discount && promoCode?.discount > 0) {
      total = total.subtract(promoCode?.discount);
    }

    return total;
  };

  const totalAmountFormatted = () => {
    return currency(totalAmount()).format();
  };

  const optionAdded = (optionID) => {
    return state?.selectedOption === optionID;
  };

  const toggleOption = (optionID) => {
    setState((prev) => {
      if (optionID === prev.selectedOption) {
        trackEvent('removed_option', optionID);

        return { ...prev, selectedOption: null };
      } else {
        trackEvent('added_option', optionID);

        return { ...prev, selectedOption: optionID };
      }
    });
  };

  return {
    startFlow,
    setStep,
    state,
    clearFlow,
    setForm,
    setClinic,
    setPricing,
    setAppointment,
    setSubscribe,
    totalAmountFormatted,
    totalAmount,
    toggleOption,
    optionAdded,
    promoCode,
    setPromoCode,
  };
};
